import { useEffect, useRef, useState, type ReactNode } from 'react';
import { App as CapApp } from '@capacitor/app';
import { Loader2, Lock, LogOut } from 'lucide-react';
import { useSettingsStore } from '@shared/stores/settingsStore';
import { useAuthStore } from '@shared/stores/authStore';
import { useBrandingStore } from '@shared/stores/brandingStore';
import { verifyBiometric } from '@shared/lib/biometric';
import { platform } from '@shared/lib/platform';
import { navigateTo } from '@shared/lib/navigation';
import { ScreenLoader } from '@shared/ui/feedback/ScreenLoader';
import { refreshSession } from '@features/auth/api';

/**
 * Holds the app back until the stored session can be shown.
 *   - Restoring: a remembered session is renewing its access token (cold start, after an
 *     unlock). A loading screen shows until the renewal settles; offline it settles at once and
 *     the app opens on the cached session.
 *   - Locked: the session's tokens are bound to the device biometric. A lock screen shows, and
 *     only a biometric match reads the tokens back. The router stays unmounted meanwhile: with no
 *     tokens in memory it would treat the member as signed out.
 *   - Re-locked: with biometric unlock on, an open session is covered again whenever the app
 *     returns from the background, until the member verifies.
 */
export function SessionGate({ children }: { children: ReactNode }): JSX.Element {
  const restoring = useAuthStore((s) => s.restoring);
  const locked = useAuthStore((s) => s.locked);
  const branding = useBrandingStore((s) => s.branding);

  const [relocked, setRelocked] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const promptedRef = useRef(false);
  // Synchronous flag the appStateChange listener can read. The Face ID / Touch
  // ID system dialog itself makes the app go inactive→active, which would
  // otherwise be mistaken for a real return-from-background and re-trigger the
  // prompt forever. While a verify is in flight we ignore those transitions.
  const verifyingRef = useRef(false);

  const unlock = async (): Promise<void> => {
    if (verifyingRef.current) return;
    verifyingRef.current = true;
    setVerifying(true);
    const reason = `Unlock ${branding?.name ?? 'your account'}`;
    try {
      // 1. A locked session: the match reads its tokens, which then renew as on any cold start.
      if (useAuthStore.getState().locked) {
        if (await useAuthStore.getState().unlock(reason)) refreshSession();
        return;
      }

      // 2. An open session back from the background: the match only verifies the member.
      if (await verifyBiometric(reason)) setRelocked(false);
    } finally {
      setVerifying(false);
      // Release a tick later so the active-state event the dialog fires on
      // dismissal is still treated as part of the prompt, not a fresh resume.
      setTimeout(() => {
        verifyingRef.current = false;
      }, 400);
    }
  };

  // Cold start: prompt once for a locked session.
  useEffect(() => {
    if (locked && !promptedRef.current) {
      promptedRef.current = true;
      void unlock();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locked]);

  // Re-lock when the app genuinely returns to the foreground.
  useEffect(() => {
    if (!platform.isNative()) return;
    let cancelled = false;
    let remove: (() => void) | undefined;
    let wasBackground = false;
    void CapApp.addListener('appStateChange', ({ isActive }) => {
      // Ignore transitions caused by the biometric dialog itself.
      if (verifyingRef.current) {
        wasBackground = false;
        return;
      }
      if (!isActive) {
        wasBackground = true;
        return;
      }
      if (wasBackground) {
        wasBackground = false;
        const auth = useAuthStore.getState();
        if (useSettingsStore.getState().biometricEnabled && (auth.isAuthenticated() || auth.locked)) {
          if (!auth.locked) setRelocked(true);
          void unlock();
        }
      }
    }).then((handle) => {
      // Cleanup may have run before the plugin handed back the handle.
      if (cancelled) void handle.remove();
      else remove = () => void handle.remove();
    });
    return () => {
      cancelled = true;
      remove?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSignOut = async (): Promise<void> => {
    await useAuthStore.getState().signOut();
    setRelocked(false);
    navigateTo('/login', { replace: true });
  };

  if (restoring) return <ScreenLoader />;

  return (
    <>
      {locked ? null : children}
      {locked || relocked ? (
        <div className="fixed inset-0 z-[60] flex flex-col items-center justify-center gap-6 bg-paper px-8 text-center pt-safe-or-6 pb-safe-or-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-50 text-primary-700">
            <Lock className="h-7 w-7" strokeWidth={1.5} />
          </span>
          <div>
            <h1 className="font-display text-display-lg text-ink">{branding?.name ?? 'Locked'}</h1>
            <p className="mt-1 text-body text-ink-secondary">
              Unlock to continue to your account.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void unlock()}
            disabled={verifying}
            className="inline-flex items-center justify-center gap-2 rounded-pill bg-primary-600 px-6 py-3 font-display text-body font-semibold text-primary-ink transition-transform duration-fast active:scale-[0.99] disabled:opacity-70"
          >
            {verifying ? (
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
            ) : (
              <Lock className="h-4 w-4" strokeWidth={2} />
            )}
            {verifying ? 'Verifying…' : 'Unlock'}
          </button>
          <button
            type="button"
            onClick={() => void onSignOut()}
            className="inline-flex items-center gap-1.5 text-body-sm font-medium text-ink-muted transition-colors hover:text-ink"
          >
            <LogOut className="h-4 w-4" strokeWidth={1.75} />
            Sign out
          </button>
        </div>
      ) : null}
    </>
  );
}
